import { makeStyles } from '@material-ui/core'
import Collapse from '../../components/Collapse'
import { useAdminIntegrationsConfig, useProcessEvent } from '../../hooks'
import type { EventType, GoogleCalendar, GoogleCalendarDetailsVisibility, GoogleCalendarSync } from 'paintscout'
import React, { useEffect, useState } from 'react'
import Grid from '../../components/Grid'
import Spinner from '../../components/Spinner'
import Typography from '../../components/Typography'
import Switch from '../../components/Switch'
import { useFormikContext } from 'formik'
import type { ProfileFormikValues } from '../ProfileFormik'
import { useSnackbar } from 'notistack'
import type { DropdownSelectOption } from '../../components/DropdownSelect/DropdownSelect'
import DropdownSelect from '../../components/DropdownSelect/DropdownSelect'

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3)
  },
  calendarItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center'
  }
}))

export interface GoogleCalendarProviderFormProps {
  isAdminIntegrations?: boolean
  companyId?: string
  userId?: string
}

export function GoogleCalendarProviderForm({
  isAdminIntegrations = false,
  companyId = '',
  userId = ''
}: GoogleCalendarProviderFormProps) {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const { processEvent } = useProcessEvent()
  const { adminIntegrationsConfig } = useAdminIntegrationsConfig()
  const { values, isSubmitting, setFieldValue, handleSubmit } = useFormikContext<ProfileFormikValues>()
  const { preferences } = values

  const [calendarsLoading, setCalendarsLoading] = useState(true)
  const [calendars, setCalendars] = useState<GoogleCalendar[]>([])
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null)
  const [calendarConfigs, setCalendarConfigs] = useState(
    preferences?.integrations?.['google-calendar']?.config?.calendars || []
  )

  const syncOptions: DropdownSelectOption[] = [
    { label: 'Two-way sync', value: 'two-way' },
    { label: 'One-way sync from PaintScout to Google', value: 'ps-to-google' },
    { label: 'One-way sync from Google to PaintScout', value: 'google-to-ps' }
  ]

  const visiblityOptions: DropdownSelectOption[] = [
    { label: 'Hide event details', value: 'hidden' },
    { label: 'Show event details to only me', value: 'own' },
    { label: 'Show event details to everyone', value: 'visible' }
  ]

  async function getCalendars(retries = 2) {
    const { result, error: responseError } = isAdminIntegrations
      ? await adminIntegrationsConfig({
          provider: 'google-calendar',
          type: 'get-calendars' as EventType,
          params: {
            companyId,
            userId
          }
        })
      : await processEvent({
          type: 'get-calendars' as EventType,
          provider: 'google-calendar',
          params: {}
        })

    const error = responseError || result.error
    if (error) {
      if (retries > 0) {
        return getCalendars(retries - 1)
      }
      console.log('Error fetching Google calendars:', error)
      enqueueSnackbar('Something went wrong, please try again later.', { variant: 'error' })
      setCalendarsLoading(false)
      return
    }
    setCalendars(result.result as GoogleCalendar[])
    setCalendarsLoading(false)
  }

  useEffect(() => {
    getCalendars()
  }, [])

  async function handleSwitchClick(checked: boolean, calendarId: string, index: number) {
    if (checked) {
      await handleConnect(calendarId, index)
    } else {
      await handleDisconnect(calendarId)
    }
  }

  async function handleConnect(calendarId: string, index: number) {
    setLoadingIndex(index)
    const { error, result } = await processEvent({
      type: 'connect-to-calendar' as EventType,
      provider: 'google-calendar',
      params: {
        calendarId
      }
    })
    const { config: calendarConfig, preferences: updatedPreferences } = result.result
    if (error) {
      console.error('Error connecting to Google Calendar:', error)
      enqueueSnackbar('Failed to connect to Google Calendar', { variant: 'error' })
      setLoadingIndex(null)
      return
    }
    const updatedConfigs = [...calendarConfigs, { ...calendarConfig, syncType: 'two-way', detailsVisibility: 'hidden' }]
    setFieldValue(`preferences`, {
      ...updatedPreferences,
      integrations: {
        ...updatedPreferences.integrations,
        'google-calendar': {
          ...updatedPreferences.integrations['google-calendar'],
          config: {
            ...updatedPreferences.integrations['google-calendar'].config,
            calendars: updatedConfigs
          }
        }
      }
    })
    setCalendarConfigs(updatedConfigs)
    setLoadingIndex(null)
  }

  const handleDisconnect = async (calendarId: string) => {
    const newConfigs = calendarConfigs.filter((c) => c.calendarId !== calendarId)
    setCalendarConfigs(newConfigs)
    setFieldValue(`preferences.integrations['google-calendar'].config.calendars`, newConfigs)
    setTimeout(() => {
      handleSubmit()
    }, 100)
  }

  const getMatchingCalendarConfig = (calendar: GoogleCalendar) => {
    const calendarId = calendar.primary ? 'primary' : calendar.id
    return calendarConfigs.find((c) => c.calendarId === calendarId)
  }

  return (
    <div className={classes.root}>
      <Collapse show={calendarsLoading}>{calendarsLoading && <Spinner />}</Collapse>
      <Collapse show={!calendarsLoading}>
        <Grid container>
          {calendars.map((calendar, index) => (
            <Grid item xs={12} key={calendar.id} className={classes.calendarItem}>
              <Typography
                weight={500}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                {calendar.primary ? `Primary (${calendar.summary})` : calendar.summary}
                {loadingIndex === index ? (
                  <div style={{ margin: '8px' }}>
                    <Spinner size={22} />
                  </div>
                ) : (
                  <Switch
                    checked={!!getMatchingCalendarConfig(calendar)}
                    onChange={(event, checked) =>
                      handleSwitchClick(checked, calendar.primary ? 'primary' : calendar.id, index)
                    }
                    disabled={isSubmitting}
                  />
                )}
              </Typography>
              {!!getMatchingCalendarConfig(calendar) && (
                <Grid container spacing={2} style={{ marginTop: '8px' }}>
                  <Grid item sm={6} xs={12}>
                    <DropdownSelect
                      label="Sync Options"
                      variant="single"
                      options={syncOptions}
                      value={
                        syncOptions.find((option) => option.value === getMatchingCalendarConfig(calendar)?.syncType) ||
                        syncOptions[0]
                      }
                      onChange={(option) => {
                        const newSyncType = option.value as GoogleCalendarSync
                        const updatedConfigs = calendarConfigs.map((c) => {
                          if (c.calendarId === (calendar.primary ? 'primary' : calendar.id)) {
                            return {
                              ...c,
                              syncType: newSyncType
                            }
                          }
                          return c
                        })
                        setCalendarConfigs(updatedConfigs)
                        setFieldValue(`preferences.integrations['google-calendar'].config.calendars`, updatedConfigs)
                      }}
                    />
                  </Grid>
                  {getMatchingCalendarConfig(calendar)?.syncType !== 'ps-to-google' && (
                    <Grid item sm={6} xs={12}>
                      <DropdownSelect
                        label="Imported Google Events Visibility"
                        variant="single"
                        options={visiblityOptions}
                        value={
                          visiblityOptions.find(
                            (option) => option.value === getMatchingCalendarConfig(calendar)?.detailsVisibility
                          ) || visiblityOptions[0]
                        }
                        onChange={(option) => {
                          const newVisibility = option.value as GoogleCalendarDetailsVisibility
                          const updatedConfigs = calendarConfigs.map((c) => {
                            if (c.calendarId === (calendar.primary ? 'primary' : calendar.id)) {
                              return {
                                ...c,
                                detailsVisibility: newVisibility
                              }
                            }
                            return c
                          })
                          setCalendarConfigs(updatedConfigs)
                          setFieldValue(`preferences.integrations['google-calendar'].config.calendars`, updatedConfigs)
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          ))}
        </Grid>
      </Collapse>
    </div>
  )
}
